import { useLayoutEffect, useState, type ReactNode } from "react";
import type {
  EnrichedOrder,
  OrderSummary,
  RemoveOrderItemResponse,
} from "@/features/order/types";
import { isLegacyDirectKgItem } from "@/lib/quantity";
import { OrderCardHeader } from "./OrderCardHeader";
import { OrderItemRow } from "./OrderItemRow";

type OrderCardProps = {
  summary: OrderSummary;
  detail: EnrichedOrder | undefined;
  isExpanded: boolean;
  isLoading: boolean;
  isError: boolean;
  onToggle: () => void;
  onRetry: () => void;
  isSuper?: boolean;
  canManageOrders?: boolean;
  showClientName?: boolean;
  isStale?: boolean;
  detailSupplement?: ReactNode;
  addItemPanel?: ReactNode;
  actionRail?: ReactNode;
  onItemRemoved?: (response: RemoveOrderItemResponse) => void;
  onFeedback?: (feedback: {
    title: string;
    message?: string;
    tone: "success" | "error";
  }) => void;
};

type OrderCardDetailsProps = Pick<
  OrderCardProps,
  | "summary"
  | "detail"
  | "isLoading"
  | "isError"
  | "onRetry"
  | "isSuper"
  | "canManageOrders"
  | "isStale"
  | "detailSupplement"
  | "addItemPanel"
  | "onItemRemoved"
  | "onFeedback"
  | "actionRail"
> & {
  readOnly: boolean;
};

function OrderCardDetails({
  summary,
  detail,
  isLoading,
  isError,
  onRetry,
  isSuper = false,
  canManageOrders = isSuper,
  isStale = false,
  detailSupplement,
  addItemPanel,
  onItemRemoved,
  onFeedback,
  readOnly,
  actionRail,
}: OrderCardDetailsProps) {
  return (
    <section
      id={`order-card-panel-${summary.id}`}
      data-testid={`order-card-details-${summary.id}`}
      className="expanded-order"
    >
      <header className="expanded-order-head">
        <h3>Itens do pedido</h3>
      </header>
      {isLoading && (
        <p className="order-card-state" role="status">
          Carregando itens...
        </p>
      )}
      {isError && !isLoading && (
        <div className="order-card-state" role="alert">
          <p>Pedido indisponível</p>
          <button type="button" onClick={onRetry}>
            Tentar novamente
          </button>
        </div>
      )}
      {!isLoading && !isError && detail?.items.length === 0 && (
        <p className="order-card-state">Sem itens neste pedido</p>
      )}
      {!isLoading && !isError && detail && detail.items.length > 0 && (
        <div className="order-card-item-list">
          {detail.items.map((item) => (
            <OrderItemRow
              key={item.id}
              item={item}
              orderId={detail.id}
              expectedVersion={detail.version}
              orderStatus={detail.status}
              isSuper={isSuper}
              canManageOrders={canManageOrders}
              isStale={isStale}
              readOnly={readOnly}
              onRemove={onItemRemoved}
              onFeedback={onFeedback}
            />
          ))}
        </div>
      )}
      {!isLoading && !isError && !readOnly && detailSupplement}
      {!isLoading && !isError && !readOnly && addItemPanel}
      {!isLoading && !isError && !readOnly && actionRail && (
        <footer className="expanded-actions">{actionRail}</footer>
      )}
    </section>
  );
}

export function OrderCard({
  summary,
  detail,
  isLoading,
  isError,
  isExpanded,
  onToggle,
  onRetry,
  isSuper = false,
  canManageOrders = isSuper,
  showClientName = isSuper,
  isStale = false,
  detailSupplement,
  addItemPanel,
  actionRail,
  onItemRemoved,
  onFeedback,
}: OrderCardProps) {
  const [detailPresent, setDetailPresent] = useState(isExpanded);
  const [isDetailExiting, setIsDetailExiting] = useState(false);
  const [presentedDetail, setPresentedDetail] = useState(detail);

  useLayoutEffect(() => {
    if (isExpanded) {
      setPresentedDetail(detail);
      setDetailPresent(true);
      setIsDetailExiting(false);
      return;
    }
    if (!detailPresent) return;

    setIsDetailExiting(true);
    const exitTimer = window.setTimeout(() => {
      setDetailPresent(false);
      setIsDetailExiting(false);
    }, 160);
    return () => window.clearTimeout(exitTimer);
  }, [detail, detailPresent, isExpanded]);

  const hasLegacyDirectKg = Boolean(detail?.items.some(isLegacyDirectKgItem));
  const isLegacyReadOnly = hasLegacyDirectKg && !isSuper;
  return (
    <article
      className="order-group"
      data-testid={`order-card-${summary.id}`}
      data-expanded={isExpanded ? "true" : "false"}
    >
      <OrderCardHeader
        order={summary}
        isSuper={isSuper}
        showClientName={showClientName}
        expanded={isExpanded}
        totalPrice={summary.total_price}
        onToggle={onToggle}
        rowActions={detailPresent ? null : (actionRail ?? null)}
      />
      {detailPresent && (
        <div
          data-testid={`order-detail-presence-${summary.id}`}
          data-state={isDetailExiting ? "exiting" : "entered"}
          className="order-card-detail-presence"
          aria-hidden={isDetailExiting || undefined}
          inert={isDetailExiting || undefined}
        >
          <OrderCardDetails
            summary={summary}
            detail={isExpanded ? detail : presentedDetail}
            isLoading={isExpanded && isLoading}
            isError={isExpanded && isError}
            onRetry={onRetry}
            isSuper={isSuper}
            canManageOrders={canManageOrders}
            isStale={isStale}
            detailSupplement={detailSupplement}
            addItemPanel={addItemPanel}
            onItemRemoved={onItemRemoved}
            onFeedback={onFeedback}
            readOnly={isLegacyReadOnly}
            actionRail={actionRail}
          />
        </div>
      )}
    </article>
  );
}
